import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Package, Plus, QrCode, AlertTriangle, CheckCircle, History, Link2, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';

const UnitGenealogyPage = () => {
  const { api } = useAuth();
  const [finishedUnits, setFinishedUnits] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [genealogyData, setGenealogyData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [consumeDialogOpen, setConsumeDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('units');

  const [consumeForm, setConsumeForm] = useState({
    unit_id: '',
    component_sku: '',
    quantity_consumed: 1,
    lot_number: '',
    serial_number: '',
    from_location: '',
    notes: '',
  });

  const [searchForm, setSearchForm] = useState({
    component_sku: '',
    lot_number: '',
    serial_number: '',
  });

  const fetchData = async () => {
    try {
      const [unitsRes, itemsRes, locsRes] = await Promise.all([
        api().get('/finished-units'),
        api().get('/items'),
        api().get('/locations')
      ]);
      setFinishedUnits(unitsRes.data);
      setItems(itemsRes.data);
      setLocations(locsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // iter67 — printable unit genealogy report (compliance / recall trace)
  const printGenealogy = async () => {
    if (!genealogyData || !selectedUnit) return;
    const cols = [
      { key: 'component_sku', label: 'Component SKU', mono: true },
      { key: 'component_name', label: 'Component' },
      { key: 'quantity_consumed', label: 'Qty', mono: true },
      { key: 'lot_number', label: 'Lot', mono: true },
      { key: 'serial_number', label: 'Serial', mono: true },
      { key: 'from_location', label: 'From' },
      { key: 'consumed_at', label: 'Consumed', fmt: (v) => v ? new Date(v).toLocaleString() : '—' },
    ];
    const consumption = genealogyData.consumed_components || genealogyData.components || [];
    const url = `${window.location.origin}/public/finished-units/${selectedUnit.public_token || selectedUnit.id}`;
    const summaryRows = [
      ['Serial Number', genealogyData.serial_number],
      ['Product', genealogyData.product_type || genealogyData.item_name],
      ['Built On', genealogyData.created_at ? new Date(genealogyData.created_at).toLocaleString() : '—'],
      ['Status', genealogyData.status || 'Active'],
      ['Total Components', String(consumption.length)],
      ['Recalled Lots', genealogyData.recall_count || 0],
    ];
    const summary = `<div class="grid-2">${summaryRows.map(([k, v]) => `
      <div><div class="label">${k}</div><div class="value mono">${v ?? '—'}</div></div>
    `).join('')}</div>`;
    await openBrandedPrintWindow({
      title: 'UNIT GENEALOGY REPORT',
      subtitle: genealogyData.serial_number,
      orientation: 'portrait',
      qrUrl: url,
      body: `${H.callout('<strong>Compliance trace:</strong> Every part consumed in this finished unit. Use this report for recalls, warranty claims, and audit responses.')}${summary}${H.sectionTitle('Consumed Components')}${H.table(cols, consumption)}${H.signoffLines('QC Sign-off', 'Date')}`,
      footerNote: 'Internal Document · Manufacturing Genealogy',
    });
  };


  useEffect(() => {
    fetchData();
  }, []);

  // iter32: deep-link `?unit=<serial>` auto-opens the unit in the genealogy view
  useEffect(() => {
    if (!finishedUnits || finishedUnits.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const serial = params.get('unit');
    if (serial) {
      const match = finishedUnits.find(u => (u.serial_number || '').toLowerCase() === serial.toLowerCase());
      if (match) {
        handleUnitSelect(match);
        setSearchTerm(serial);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedUnits]);

  const fetchUnitGenealogy = async (unitId) => {
    try {
      const [genealogyRes, comparisonRes] = await Promise.all([
        api().get(`/genealogy/unit/${unitId}`),
        api().get(`/genealogy/unit/${unitId}/bom-comparison`)
      ]);
      setGenealogyData(genealogyRes.data);
      setComparisonData(comparisonRes.data);
    } catch (error) {
      toast.error('Failed to fetch genealogy');
    }
  };

  const handleUnitSelect = (unit) => {
    setSelectedUnit(unit);
    setConsumeForm({ ...consumeForm, unit_id: unit.id });
    fetchUnitGenealogy(unit.id);
  };

  const handleConsumeSubmit = async (e) => {
    e.preventDefault();
    try {
      await api().post('/genealogy/consume', {
        ...consumeForm,
        quantity_consumed: parseFloat(consumeForm.quantity_consumed),
        lot_number: consumeForm.lot_number || null,
        serial_number: consumeForm.serial_number || null,
        from_location: consumeForm.from_location || null,
      });
      toast.success('Component consumption recorded');
      setConsumeDialogOpen(false);
      resetConsumeForm();
      if (selectedUnit) {
        fetchUnitGenealogy(selectedUnit.id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record consumption');
    }
  };

  const handleSearch = async () => {
    if (!searchForm.component_sku && !searchForm.lot_number && !searchForm.serial_number) {
      toast.error('Enter at least one search criteria');
      return;
    }
    try {
      const params = new URLSearchParams();
      if (searchForm.component_sku) params.append('component_sku', searchForm.component_sku);
      if (searchForm.lot_number) params.append('lot_number', searchForm.lot_number);
      if (searchForm.serial_number) params.append('serial_number', searchForm.serial_number);
      
      const res = await api().get(`/genealogy/search?${params.toString()}`);
      setSearchResults(res.data);
      toast.success(`Found ${res.data.affected_units} affected units`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    }
  };

  const resetConsumeForm = () => {
    setConsumeForm({
      unit_id: selectedUnit?.id || '',
      component_sku: '',
      quantity_consumed: 1,
      lot_number: '',
      serial_number: '',
      from_location: '',
      notes: '',
    });
  };

  const filteredUnits = finishedUnits.filter(unit =>
    unit.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OK': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'Over': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'Under': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'Extra': return <AlertTriangle className="w-4 h-4 text-purple-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="unit-genealogy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Unit Genealogy</h1>
          <p className="text-slate-500 text-sm">Track component consumption and traceability</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="units">
            <Package className="w-4 h-4 mr-2" />
            Unit Tracking
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="w-4 h-4 mr-2" />
            Component Search
          </TabsTrigger>
        </TabsList>

        {/* Unit Tracking Tab */}
        <TabsContent value="units" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Unit List */}
            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="space-y-2">
                  <CardTitle className="text-lg">Finished Units</CardTitle>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by serial..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 rounded-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[500px] overflow-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="spinner" />
                  </div>
                ) : filteredUnits.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {filteredUnits.map((unit) => (
                      <div
                        key={unit.id}
                        onClick={() => handleUnitSelect(unit)}
                        className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedUnit?.id === unit.id ? 'bg-slate-100' : ''
                        }`}
                      >
                        <p className="font-mono font-semibold text-sm">{unit.serial_number}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(unit.build_date).toLocaleDateString()} • {unit.current_status}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No units found</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Genealogy Details */}
            <Card className="lg:col-span-2 industrial-card">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {genealogyData ? `Genealogy - ${genealogyData.serial_number}` : 'Select a Unit'}
                  </CardTitle>
                  {selectedUnit && (
                    <div className="flex gap-1">
                      <Button
                        onClick={() => printGenealogy()}
                        variant="outline"
                        className="rounded-sm"
                        size="sm"
                        data-testid="genealogy-print-btn"
                        disabled={!genealogyData}
                      >
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                      <Button
                        onClick={() => setConsumeDialogOpen(true)}
                        className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                        size="sm"
                        data-testid="add-consumption-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Component
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {genealogyData ? (
                  <div className="space-y-6">
                    {/* Unit Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs text-slate-500">Product Type</p>
                        <p className="font-medium">{genealogyData.product_type}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs text-slate-500">BOM</p>
                        <p className="font-mono">{genealogyData.bom_variant} v{genealogyData.bom_revision}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs text-slate-500">Built By</p>
                        <p className="font-medium">{genealogyData.built_by}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs text-slate-500">Build Date</p>
                        <p className="font-medium">{new Date(genealogyData.build_date).toLocaleDateString()}</p>
                      </div>
                    </div>

                    {/* Cost Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-sm text-center">
                        <p className="text-2xl font-bold text-blue-700 mono">${genealogyData.total_component_cost}</p>
                        <p className="text-xs text-blue-600">Component Cost</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-sm text-center">
                        <p className="text-2xl font-bold text-green-700 mono">${genealogyData.total_labor_cost}</p>
                        <p className="text-xs text-green-600">Labor Cost</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-sm text-center">
                        <p className="text-2xl font-bold text-purple-700 mono">${genealogyData.total_unit_cost}</p>
                        <p className="text-xs text-purple-600">Total Unit Cost</p>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex justify-center">
                      <div className="bg-white p-4 border border-slate-200 rounded-sm inline-block">
                        <QRCodeSVG 
                          value={`${window.location.origin}/genealogy/${genealogyData.serial_number}`}
                          size={100}
                          level="H"
                        />
                        <p className="text-center mt-2 font-mono text-sm">{genealogyData.serial_number}</p>
                      </div>
                    </div>

                    {/* Components Consumed */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Components Consumed ({genealogyData.total_components})
                      </h4>
                      {genealogyData.components_consumed.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Component</TableHead>
                              <TableHead>Qty</TableHead>
                              <TableHead>Lot/Serial</TableHead>
                              <TableHead>Consumed At</TableHead>
                              <TableHead>By</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {genealogyData.components_consumed.map((c, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <p className="font-mono text-sm">{c.component_sku}</p>
                                  <p className="text-xs text-slate-500">{c.item_name}</p>
                                </TableCell>
                                <TableCell className="mono">{c.quantity_consumed}</TableCell>
                                <TableCell className="mono text-sm">{c.lot_number || c.serial_number || '-'}</TableCell>
                                <TableCell className="text-sm">{new Date(c.consumed_at).toLocaleString()}</TableCell>
                                <TableCell className="text-sm">{c.consumed_by_name}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-slate-500 text-sm">No components recorded yet</p>
                      )}
                    </div>

                    {/* BOM Comparison */}
                    {comparisonData && comparisonData.comparison.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Link2 className="w-4 h-4" />
                          BOM Comparison
                        </h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Status</TableHead>
                              <TableHead>Component</TableHead>
                              <TableHead>Required</TableHead>
                              <TableHead>Consumed</TableHead>
                              <TableHead>Variance</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {comparisonData.comparison.map((c, idx) => (
                              <TableRow key={idx} className={c.status !== 'OK' ? 'bg-amber-50' : ''}>
                                <TableCell>{getStatusIcon(c.status)}</TableCell>
                                <TableCell>
                                  <p className="font-mono text-sm">{c.component_sku}</p>
                                  <p className="text-xs text-slate-500">{c.component_name}</p>
                                </TableCell>
                                <TableCell className="mono">{c.required_qty} {c.uom}</TableCell>
                                <TableCell className="mono">{c.consumed_qty} {c.uom}</TableCell>
                                <TableCell className={`mono font-semibold ${
                                  c.variance > 0 ? 'text-orange-600' : c.variance < 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {c.variance > 0 ? '+' : ''}{c.variance}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                    <QrCode className="w-16 h-16 mb-4" />
                    <p>Select a unit to view its genealogy</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Component Search Tab */}
        <TabsContent value="search" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Search by Component</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Component SKU</Label>
                  <Select value={searchForm.component_sku || "any"} onValueChange={(v) => setSearchForm({ ...searchForm, component_sku: v === "any" ? "" : v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select component" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      {items.map(item => (
                        <SelectItem key={item.item_sku} value={item.item_sku}>{item.item_sku} - {item.item_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lot Number</Label>
                  <Input
                    value={searchForm.lot_number}
                    onChange={(e) => setSearchForm({ ...searchForm, lot_number: e.target.value })}
                    placeholder="Enter lot number"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input
                    value={searchForm.serial_number}
                    onChange={(e) => setSearchForm({ ...searchForm, serial_number: e.target.value })}
                    placeholder="Enter serial number"
                    className="rounded-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSearch} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>

              {searchResults && (
                <div className="mt-6">
                  <div className="bg-slate-50 p-4 rounded-sm mb-4">
                    <p className="font-semibold">Search Results</p>
                    <p className="text-sm text-slate-600">
                      Found {searchResults.total_consumptions} consumption records affecting {searchResults.affected_units} units
                    </p>
                  </div>
                  {searchResults.units.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Serial Number</TableHead>
                          <TableHead>Product Type</TableHead>
                          <TableHead>Build Date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.units.map((unit) => (
                          <TableRow key={unit.unit_id}>
                            <TableCell className="font-mono font-medium">{unit.serial_number}</TableCell>
                            <TableCell>{unit.product_type}</TableCell>
                            <TableCell>{new Date(unit.build_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <span className={`status-badge ${unit.current_status === 'InStock' ? 'status-released' : 'status-draft'}`}>
                                {unit.current_status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Consume Component Dialog */}
      <Dialog open={consumeDialogOpen} onOpenChange={(open) => { setConsumeDialogOpen(open); if (!open) resetConsumeForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Component Consumption</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConsumeSubmit} className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-sm">
              <p className="text-xs text-slate-500">Recording for unit</p>
              <p className="font-mono font-semibold">{selectedUnit?.serial_number}</p>
            </div>
            <div className="space-y-2">
              <Label>Component *</Label>
              <Select value={consumeForm.component_sku} onValueChange={(v) => setConsumeForm({ ...consumeForm, component_sku: v })}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select component" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.item_sku} value={item.item_sku}>
                      {item.item_sku} - {item.item_name} ({item.current_quantity} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={consumeForm.quantity_consumed}
                  onChange={(e) => setConsumeForm({ ...consumeForm, quantity_consumed: e.target.value })}
                  required
                  className="rounded-sm mono"
                />
              </div>
              <div className="space-y-2">
                <Label>From Location</Label>
                <Select value={consumeForm.from_location || "none"} onValueChange={(v) => setConsumeForm({ ...consumeForm, from_location: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.location_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lot Number</Label>
                <Input
                  value={consumeForm.lot_number}
                  onChange={(e) => setConsumeForm({ ...consumeForm, lot_number: e.target.value })}
                  placeholder="Optional"
                  className="rounded-sm mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={consumeForm.serial_number}
                  onChange={(e) => setConsumeForm({ ...consumeForm, serial_number: e.target.value })}
                  placeholder="Optional"
                  className="rounded-sm mono"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setConsumeDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" disabled={!consumeForm.component_sku}>
                Record Consumption
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitGenealogyPage;
